/**
 * FilterChecklistSection — inline, self-contained checklist with a search input.
 *
 * Used for high-cardinality whitelist dimensions (Fach, Klasse, ...). Renders
 * entirely inline (no nested popovers) so the drawer stays the final layer.
 */
import { useMemo, useState } from 'react'

export interface ChecklistOption {
  value: string
  label: string
  count?: number
}

interface Props {
  title: string
  options: ChecklistOption[]
  selected: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  searchPlaceholder?: string
  labelAll?: string
  labelNoneRestriction?: string
  emptyHint?: string
  noHitsLabel?: string
}

export function FilterChecklistSection({
  title,
  options,
  selected,
  onChange,
  disabled,
  searchPlaceholder = 'Suchen…',
  labelAll = 'Alle',
  labelNoneRestriction = 'Keine Einschränkung',
  emptyHint = 'Alle sichtbar (keine eingeschränkt)',
  noHitsLabel = 'Keine Treffer',
}: Props) {
  const [query, setQuery] = useState('')
  const selectedSet = useMemo(() => new Set(selected), [selected])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q))
  }, [options, query])

  const allSelected = options.length > 0 && options.every((o) => selectedSet.has(o.value))
  const noneSelected = selectedSet.size === 0

  const toggle = (value: string) => {
    if (disabled) return
    const next = new Set(selectedSet)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    onChange(Array.from(next))
  }

  return (
    <div className={`mb-3 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">{title}</span>
        <div className="flex gap-2 text-[10px]">
          <button
            type="button"
            onClick={() => onChange(allSelected ? [] : options.map((o) => o.value))}
            className="text-blue-600 hover:underline disabled:opacity-40"
            disabled={disabled}
          >
            {allSelected ? labelNoneRestriction : labelAll}
          </button>
        </div>
      </div>
      {options.length > 6 && (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1 mb-1 bg-white/70 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      )}
      <div className="max-h-32 overflow-y-auto pr-1 space-y-0.5">
        {filtered.length === 0 && (
          <div className="text-[11px] text-gray-400 italic py-1">{noHitsLabel}</div>
        )}
        {filtered.map((o) => {
          return (
            <label
              key={o.value}
              className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:bg-white/60 rounded px-1 py-0.5"
            >
              <input
                type="checkbox"
                checked={selectedSet.has(o.value)}
                onChange={() => toggle(o.value)}
                disabled={disabled}
                className="accent-blue-500"
              />
              <span className="flex-1 truncate">{o.label}</span>
              {typeof o.count === 'number' && (
                <span className="text-[10px] text-gray-400">{o.count}</span>
              )}
            </label>
          )
        })}
      </div>
      {noneSelected && options.length > 0 && (
        <div className="text-[10px] text-amber-600 mt-0.5">{emptyHint}</div>
      )}
    </div>
  )
}
